import React, { useEffect, useRef, useState } from "react";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";
// Assuming these are correctly imported from your constants file
import { AUTH_TOKEN, ORG_ID, DOMAIN_ID, REACT_APP_API } from "../../../constants";

// ANSI color codes
const colors = {
  reset: "\x1b[0m",
  yellow: "\x1b[33m",
  green: "\x1b[32m",
  red: "\x1b[31m",
  cyan: "\x1b[36m",
};

// Define the specific string that indicates the session ended via 'exit' command
const EXIT_INDICATOR_STRING = "Terminal read error";

function MySqlTerminal({ connectionString, onTerminalExit }) {
  const terminalRef = useRef(null);
  const xtermRef = useRef(null);
  const fitAddonRef = useRef(null);
  const socketRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState("initializing");
  // Ref to track if the initial "approval" screen clear and message update has happened.
  const isApprovedAndScreenClearedRef = useRef(false);

  // Function to send terminal size to server
  const sendSize = (socket) => {
    if (socket && socket.readyState === WebSocket.OPEN && xtermRef.current) {
      socket.send(
        JSON.stringify({
          type: "resize",
          data: {
            rows: xtermRef.current.rows,
            cols: xtermRef.current.cols,
          },
        })
      );
    }
  };

  useEffect(() => {
    // Reset the approval flag each time the effect runs (e.g., connectionString changes)
    isApprovedAndScreenClearedRef.current = false;

    const term = new Terminal({
      cursorBlink: true,
      macOptionIsMeta: true,
      scrollback: 1000,
      theme: {
        background: "#000",
        foreground: "#f0f0f0",
      },
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
    });
    xtermRef.current = term;

    const fitAddon = new FitAddon();
    fitAddonRef.current = fitAddon;
    term.loadAddon(fitAddon);

    if (terminalRef.current) {
      term.open(terminalRef.current);
      setTimeout(() => {
        if (fitAddonRef.current && xtermRef.current?.element) {
          fitAddonRef.current.fit();
        }
      }, 50);
    }

    const initializeTerminal = async () => {
      try {
        setConnectionStatus("initializing");
        // Ensure isApprovedAndScreenClearedRef is false at the very start of an attempt
        isApprovedAndScreenClearedRef.current = false;
        term.write(
          `\r\n${colors.yellow}Initializing terminal session with server...${colors.reset}\r\n`
        );

        const initUrl = `${REACT_APP_API}/console/init`;
        const orgIdValue = typeof ORG_ID === "function" ? ORG_ID() : ORG_ID;
        const domainIdValue = typeof DOMAIN_ID === "function" ? DOMAIN_ID() : DOMAIN_ID;
        const authTokenValue = typeof AUTH_TOKEN === "function" ? AUTH_TOKEN() : AUTH_TOKEN;

        const response = await fetch(initUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            command: connectionString,
            org_id: orgIdValue,
            tenant_id: domainIdValue,
            sessionId: authTokenValue,
          }),
        });
        console.log("Init Response Status:", response.status);

        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(
            `Failed to initialize terminal session (HTTP ${response.status}). ${errorBody}`
          );
        }

        term.write(
          `\r\n${colors.yellow}Server session active. Establishing secure WebSocket connection...${colors.reset}\r\n`
        );

        const wsBaseUrl = `${REACT_APP_API}`.replace(/^http/, "ws");
        const socket = new WebSocket(
          `${wsBaseUrl}/console/ws?sessionId=` + encodeURIComponent(authTokenValue)
        );
        socketRef.current = socket;

        socket.onopen = () => {
          setConnectionStatus("connected");
          isApprovedAndScreenClearedRef.current = false; // Explicitly reset on new WebSocket open

          term.write("\r\n\x1b[2J\x1b[H"); // Clear screen
          term.write(`\r\n${colors.green}Database Connection successful!${colors.reset}\r\n`);
          term.write(
            `${colors.cyan}------------------------------------------------${colors.reset}\r\n`
          );
          term.write(
            `                 ${colors.cyan}authnull${colors.reset}                       \r\n`
          );
          term.write(
            `${colors.cyan}------------------------------------------------${colors.reset}\r\n`
          );
          term.write(
            `\r\n${colors.yellow}Please check your Authnull authenticator app for approval to access the terminal.${colors.reset}\r\n\r\n`
          );
          sendSize(socket);
        };

        socket.onmessage = (event) => {
          let parsedMessage;
          let messageType = null; // 'data', 'error', or null if unparseable/unknown
          let messageContent = null;
          let isExitIndicated = false;

          // 1. Try to parse as JSON, then fallback to raw string
          try {
            parsedMessage = JSON.parse(event.data);
            if (parsedMessage.type === "data") {
              messageType = "data";
              messageContent = parsedMessage.data;
            } else if (parsedMessage.type === "error") {
              messageType = "error";
              messageContent = parsedMessage.data || "Unknown server error";
            } else {
              // Unknown JSON structure, treat as raw data if possible
              console.warn("Received unknown JSON structure:", parsedMessage);
              messageType = "data"; // Default to data if structure is odd
              messageContent = event.data.toString(); // Get raw string
            }
          } catch (e) {
            // Not JSON, treat as raw string data
            if (typeof event.data === "string") {
              messageType = "data"; // Assume raw strings are data
              messageContent = event.data;
            } else {
              console.warn("Received non-string/non-JSON WebSocket message:", event.data);
              return; // Cannot process this message
            }
          }

          if (messageContent === null || messageContent === undefined) {
            console.warn("Message content is null or undefined after parsing:", event.data);
            return; // Cannot process if content is missing
          }

          // 2. Check for exit indicator in the content string
          if (
            typeof messageContent === "string" &&
            messageContent.includes(EXIT_INDICATOR_STRING)
          ) {
            isExitIndicated = true;
          }

          // 3. Handle exit if indicated (takes precedence)
          if (isExitIndicated) {
            setConnectionStatus("exiting");
            // Write the message that contained the exit string, as it might be the error itself
            if (messageType === "data") {
              term.write(messageContent); // Write the data containing the exit string
            } else if (messageType === "error") {
              // Error message already contains the EXIT_INDICATOR_STRING
              term.write(`\r\n${colors.red}${messageContent}${colors.reset}\r\n`);
            }
            term.write(
              `\r\n${colors.yellow}Terminal session ended (detected exit string). Closing connection...${colors.reset}\r\n`
            );
            if (typeof onTerminalExit === "function") {
              setTimeout(() => onTerminalExit(), 1500);
            }
            return; // Exit processed, no further action on this message
          }

          // 4. Handle regular data or error messages (non-exit)
          if (messageType === "error") {
            term.write(`\r\n${colors.red}Error from server: ${messageContent}${colors.reset}\r\n`);
          } else if (messageType === "data") {
            if (!isApprovedAndScreenClearedRef.current) {
              // This is the first "real" data after connection, indicating approval
              isApprovedAndScreenClearedRef.current = true;
              term.write("\r\n\x1b[2J\x1b[H"); // Clear screen
              // Re-display welcome messages
              term.write(`\r\n${colors.green}WebSocket Connection successful!${colors.reset}\r\n`);
              term.write(
                `${colors.cyan}------------------------------------------------${colors.reset}\r\n`
              );
              term.write(
                `                 ${colors.cyan}authnull${colors.reset}                       \r\n`
              );
              term.write(
                `${colors.cyan}------------------------------------------------${colors.reset}\r\n`
              );
              term.write(
                `\r\n${colors.green}Web console session approved and ready.${colors.reset}\r\n\r\n`
              );
              term.write(messageContent); // Write the actual first data (e.g., prompt)
            } else {
              // Subsequent data messages
              term.write(messageContent);
            }
          }
        };

        socket.onclose = (event) => {
          if (connectionStatus !== "exiting") {
            setConnectionStatus("disconnected");
            let reason = event.reason ? ` Reason: ${event.reason}` : "";
            term.write(
              `\r\n${colors.red}Connection closed ${
                event.wasClean ? "cleanly" : "unexpectedly"
              } (Code: ${event.code}).${reason}${colors.reset}\r\n`
            );
            if (!event.wasClean) {
              term.write(
                `\r\n${colors.yellow}Please refresh the page to try again.${colors.reset}\r\n`
              );
            }
          } else {
            console.log("WebSocket closed after user exit command was processed.");
          }
        };

        socket.onerror = (error) => {
          if (connectionStatus !== "exiting") {
            setConnectionStatus("error");
          }
          console.error("WebSocket error:", error);
          if (connectionStatus !== "exiting" && xtermRef.current) {
            term.write(`\r\n${colors.red}WebSocket connection error.${colors.reset}\r\n`);
            term.write(
              `\r\n${colors.yellow}Debugging: Check internet, firewall/proxy, Authnull session. Refresh if needed.${colors.reset}\r\n`
            );
          }
        };

        term.onData((data) => {
          if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify({ type: "data", data }));
          } else {
            console.warn(
              "WebSocket not open. Cannot send data. State:",
              socketRef.current?.readyState
            );
          }
        });
      } catch (error) {
        setConnectionStatus("error");
        console.error("Failed to setup terminal:", error);
        if (xtermRef.current) {
          term.write(
            `\r\n${colors.red}Failed to setup terminal: ${error.message}${colors.reset}\r\n`
          );
          term.write(
            `\r\n${colors.yellow}Tips: Check connection, API endpoint, policy, console (F12), and refresh.${colors.reset}\r\n`
          );
        }
      }
    };

    initializeTerminal();

    const resizeObserver = new ResizeObserver(() => {
      if (fitAddonRef.current && xtermRef.current?.element) {
        fitAddonRef.current.fit();
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
          sendSize(socketRef.current);
        }
      }
    });

    if (terminalRef.current) {
      resizeObserver.observe(terminalRef.current);
      resizeObserverRef.current = resizeObserver;
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (socketRef.current) {
        socketRef.current.onopen = null;
        socketRef.current.onmessage = null;
        socketRef.current.onclose = null;
        socketRef.current.onerror = null;
        if (
          socketRef.current.readyState === WebSocket.OPEN ||
          socketRef.current.readyState === WebSocket.CONNECTING
        ) {
          socketRef.current.close();
        }
        socketRef.current = null;
      }
      if (xtermRef.current) {
        xtermRef.current.dispose();
        xtermRef.current = null;
      }
    };
  }, [connectionString, onTerminalExit]);

  return (
    <div
      ref={terminalRef}
      style={{
        width: "100%",
        height: "100%",
        background: "#000",
        borderRadius: "4px",
        overflow: "hidden",
        padding: "8px",
      }}
      className="mysql-terminal-container"
    >
      <style jsx global>{`
        .mysql-terminal-container .xterm-viewport {
          scrollbar-width: thin;
          scrollbar-color: #444 #0a0a0a;
        }
        .mysql-terminal-container .xterm-viewport::-webkit-scrollbar {
          width: 10px;
        }
        .mysql-terminal-container .xterm-viewport::-webkit-scrollbar-track {
          background: #0a0a0a;
          border-radius: 5px;
        }
        .mysql-terminal-container .xterm-viewport::-webkit-scrollbar-thumb {
          background-color: #444;
          border-radius: 5px;
          border: 2px solid #0a0a0a;
        }
        .mysql-terminal-container .xterm-viewport::-webkit-scrollbar-thumb:hover {
          background-color: #555;
        }
      `}</style>
    </div>
  );
}

export default MySqlTerminal;
